import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import {
    userActionResultFromRestV1,
    userCreateToRestV1,
    userFromRestV1,
    usersFromRestV1,
    userUpdateToRestV1,
} from 'domain/mappers/userFromRestV1';
import type { CreateUserInputFE, UpdateUserInputFE, UserActionResultFE, UserFE } from 'domain/systemAdmin';

import {
    errorHandler,
    formatAxiosErrorRedacted,
    isAxiosError,
    type RejectedRequestError,
    toRejectedRequestError,
} from './errors';
import { correlationHeaders, managementRestApi } from './restClient';

/**
 * `UsersApi` over REST. Every handler reports through `formatAxiosErrorRedacted`, never
 * `formatAxiosError`: a failed write can echo the submitted personal data back in the body.
 */
const loggable = (error: unknown) => (isAxiosError(error) ? formatAxiosErrorRedacted(error) : error);

const getUserQueryFn = async (id: string): Promise<{ data: UserFE }> => {
    try {
        const response = await managementRestApi.get<unknown>(`/api/v1/users/${id}`, correlationHeaders());
        return { data: userFromRestV1(response.data) };
    } catch (error) {
        throw errorHandler('[getUserById]: Unexpected error occurred while fetching user', loggable(error));
    }
};

const listUserQueryFN = async (): Promise<{ data: UserFE[] }> => {
    try {
        const response = await managementRestApi.get<unknown>('/api/v1/users', correlationHeaders());
        return { data: usersFromRestV1(response.data) };
    } catch (error) {
        throw errorHandler('[listUsers]: Unexpected error occurred while fetching users list', loggable(error));
    }
};

const createUserFn = async (input: CreateUserInputFE): Promise<{ data: UserFE }> => {
    try {
        const response = await managementRestApi.post<unknown>(
            '/api/v1/users',
            userCreateToRestV1(input),
            correlationHeaders()
        );
        return { data: userFromRestV1(response.data) };
    } catch (error) {
        throw errorHandler('[createUser]: Unexpected error occurred while creating user', loggable(error));
    }
};

/** `PUT`, not `PATCH` — confirmed from the generated `UsersApi.updateUserById`. */
const editUserFn = async (input: UpdateUserInputFE): Promise<{ data: UserFE }> => {
    try {
        const response = await managementRestApi.put<unknown>(
            `/api/v1/users/${input.id}`,
            userUpdateToRestV1(input),
            correlationHeaders()
        );
        return { data: userFromRestV1(response.data) };
    } catch (error) {
        throw errorHandler('[updateUser]: Unexpected error occurred while updating user', loggable(error));
    }
};

/**
 * The two destructive mutations RETURN their error instead of throwing, because `SystemUsersTable`
 * shows the real HTTP status: RTK Query reduces a thrown error to name/message/stack and drops the
 * status with it, while a returned `{ error }` reaches `.unwrap()` intact.
 */
type UserActionResult = { data: UserActionResultFE } | { error: RejectedRequestError };

const resetPasswordFn = async (id: string): Promise<UserActionResult> => {
    try {
        const response = await managementRestApi.post<unknown>(
            `/api/v1/users/reset-password/${id}`,
            undefined,
            correlationHeaders()
        );
        return { data: userActionResultFromRestV1(response.status) };
    } catch (error) {
        const reported = errorHandler(
            '[resetPassword]: Unexpected error occurred while resetting user password',
            loggable(error)
        );
        return { error: toRejectedRequestError(reported) };
    }
};

const deleteUserFn = async (id: string): Promise<UserActionResult> => {
    try {
        const response = await managementRestApi.delete<unknown>(`/api/v1/users/${id}`, correlationHeaders());
        return { data: userActionResultFromRestV1(response.status) };
    } catch (error) {
        const reported = errorHandler('[deleteUser]: Unexpected error occurred while deleting user', loggable(error));
        return { error: toRejectedRequestError(reported) };
    }
};

export const userApi = createApi({
    reducerPath: 'users',
    // Every endpoint runs on `queryFn`, so this is never invoked — and keeping a GraphQL base
    // query on a slice that issues no GraphQL request would be misleading.
    baseQuery: fakeBaseQuery<unknown>(),
    tagTypes: ['Users'],
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        createUser: builder.mutation<UserFE, CreateUserInputFE>({
            queryFn: createUserFn,
            invalidatesTags: ['Users'],
        }),
        editUser: builder.mutation<UserFE, UpdateUserInputFE>({
            queryFn: editUserFn,
            invalidatesTags: ['Users'],
        }),
        resetPasswordById: builder.mutation<UserActionResultFE, string>({
            queryFn: resetPasswordFn,
        }),
        deleteUserById: builder.mutation<UserActionResultFE, string>({
            queryFn: deleteUserFn,
            invalidatesTags: ['Users'],
        }),
        getUser: builder.query<UserFE, string>({
            queryFn: getUserQueryFn,
        }),
        // Stays on Amplify: `fetchUserAttributes` reads the Cognito identity attributes off the
        // current session. MS's `/api/v1/auth/users/{userId}/claims` returns authorization
        // patterns, and needs the `sub` this call is what produces — so it cannot replace it.
        getUserAttributes: builder.query({
            queryFn: async () => {
                try {
                    const userAttributes = await fetchUserAttributes();
                    return { data: userAttributes };
                } catch (error) {
                    throw errorHandler('Unexpected error occurred while fetching the user info', error);
                }
            },
        }),
        // The `string` arg is the cache key, not an input — `'ListUsers'`, unchanged from the
        // GraphQL endpoint. Do not "clean up" the unread argument.
        listUsers: builder.query<UserFE[], string>({
            queryFn: listUserQueryFN,
            providesTags: ['Users'],
        }),
    }),
});

export const {
    useEditUserMutation,
    useCreateUserMutation,
    useGetUserQuery,
    useGetUserAttributesQuery,
    useListUsersQuery,
    useResetPasswordByIdMutation,
    useDeleteUserByIdMutation,
} = userApi;
